import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { reportFreshFindUnlockAuth } from '../freshFind/freshFindUnlockFunnel';
import { gaAuthCompleted, gaAuthConfirmationShown } from '../lib/gaPixel';

/**
 * The post-auth confirmation surface (login-signin-or-create-account-v4-spec §3.3).
 *
 * ONE non-blocking, dismissible confirmation, shown ONLY after the real outcome
 * of a direct sign-in / create-account is known — never inferred from the entered
 * email or the button tapped, and never claiming a board is saved before the
 * link actually succeeded (§9 release guard). The message is a pure function of
 * the verified `AuthOutcome`, so the honesty guarantee lives in `mapAuthConfirmation`
 * and is unit-testable without mounting anything.
 *
 * WHY IT LIVES HERE (auth/, not v4/)
 * ----------------------------------
 * Both `V4SignInModal` (the synchronous popup / email path) and `AuthGateContext`
 * (the mobile-OAuth REDIRECT return leg) must be able to fire it. A v4/ module may
 * not import `AuthGateContext` (the `v4AccountDoorConsistency` fitness invariant),
 * so the shared surface sits in auth/ — importable by both without a cycle, exactly
 * like `v4Auth` and `redirectOutcome`. It carries NO firebase/callable imports, so
 * a static import from AuthGate stays cheap (unlike the lazily-loaded V4SignInModal).
 *
 * THE ONE RULE, RECONCILED
 * ------------------------
 * The v4 normal door has NO merge path: signing into a PRE-EXISTING account leaves
 * the guest board behind (`feedback_no_merge_into_existing_account`). So the spec's
 * `guestData: "merged"` outcome never occurs here — an existing-account sign-in is
 * always `guestData: "none"`, and the mapper deliberately shows NO "board saved"
 * for it. "Board saved" is emitted only for `linked` (a brand-new account keeping
 * the guest board on the same uid) — the one case where the claim is true. The
 * `merged` branch is retained so a future merge path would map correctly.
 */

export type AuthAccount = 'created' | 'existing';
/** `merged` is unreachable in the v4 normal door (see docblock); kept for a future
 *  merge path so the contract is complete rather than lying by omission. */
export type AuthGuestData = 'none' | 'linked' | 'merged';
export type AuthEntryContext = 'generic' | 'guest_board' | 'claim';

export interface AuthOutcome {
  verificationEmail?: 'sent' | 'failed';
  account: AuthAccount;
  guestData: AuthGuestData;
  entryContext: AuthEntryContext;
  /** Guest boards in play at auth time — for singular/plural copy only. */
  boardCount?: number;
}

export type AuthConfirmationKind = 'account_ready' | 'board_saved' | 'welcome_back';

export interface AuthConfirmationMessage {
  kind: AuthConfirmationKind;
  message: string;
}

/**
 * The honesty core. Pure, exhaustive, and the single source of the rule that a
 * board is called saved ONLY when it actually was (`linked`/`merged`).
 *
 *   claim                       → null           (the claim route owns its own UI)
 *   guest data linked/merged    → "Your board(s) is saved to your account."
 *   new account, no guest data  → "Your account is ready."
 *   existing account + had board→ "welcome_back"  (the loss cohort — board left
 *                                                   behind; navigate home + welcome)
 *   generic existing sign-in    → null            (nothing transferred, nothing lost,
 *                                                   nothing to say)
 */
export function mapAuthConfirmation(outcome: AuthOutcome): AuthConfirmationMessage | null {
  const base = mapBaseAuthConfirmation(outcome);
  if (!outcome.verificationEmail) return base;
  const verification = outcome.verificationEmail === 'sent'
    ? 'Check your email to verify your account.'
    : 'Your account was created. Resend the verification email from You or your account menu.';
  return { kind: base?.kind ?? 'account_ready', message: base ? `${base.message} ${verification}` : verification };
}

function mapBaseAuthConfirmation(outcome: AuthOutcome): AuthConfirmationMessage | null {
  if (outcome.entryContext === 'claim') return null;

  const boardCount = outcome.boardCount ?? 0;

  // "Board saved" requires a board to have ACTUALLY been carried over. guestData
  // 'linked' only says the anonymous SESSION was upgraded in place — which is true
  // even for a visitor with zero boards (every fresh visitor is anon, so any new
  // account is a link). Gating on boardCount>0 is what keeps a no-board signup from
  // falsely claiming a board was saved (§9 release guard) and lets that case fall
  // through to the honest "Your account is ready." below.
  if ((outcome.guestData === 'linked' || outcome.guestData === 'merged') && boardCount > 0) {
    return {
      kind: 'board_saved',
      message:
        boardCount > 1
          ? 'Your boards are saved to your account.'
          : 'Your board is saved to your account.',
    };
  }

  if (outcome.account === 'created') {
    return { kind: 'account_ready', message: 'Your account is ready.' };
  }

  // THE LOSS COHORT (Journey 8, Decision 7). A guest who had a board and signed
  // into an account they ALREADY have — the guest board is left behind (no merge,
  // the One Rule). Do NOT strand them on the now-orphaned board URL: welcome them
  // to the account they just proved they own. The provider navigates home to their
  // boards + shows this note (mirrors V4ClaimPage's "You already have an account →
  // See your boards" existing-account exit, which the emailed-claim path already
  // has; this closes the same gap for the DIRECT sign-in path). Generic existing
  // sign-in (no guest board) stays silent — nothing was lost, nothing to say.
  if (outcome.account === 'existing' && outcome.entryContext === 'guest_board') {
    return { kind: 'welcome_back', message: 'You already have an account — here are your boards.' };
  }

  return null;
}

/* ── Naming the account on the mobile REDIRECT leg (THE-712) ───────────────
 * The desktop popup paths in `v4Auth` state the outcome outright, because the
 * call that produced it is right there: `linkWithPopup` succeeding IS a new
 * account (CREATED_LINKED); `signInWithPopup` consults `isNewUser`. The redirect
 * leg lost that context across the navigation and consulted `isNewUser` for
 * BOTH — which is wrong for a link, because Identity Toolkit attaches the
 * identity to the uid that already existed and so reports `isNewUser: false`
 * for a brand-new account. A first-time Google user was told "You already have
 * an account — here are your boards" and, because that is the `welcome_back`
 * kind, was navigated off the board they had just made.
 *
 * This restores the popup leg's reasoning from the redirect marker, so both
 * doors derive the outcome the same way:
 *
 *   link + recovered   → existing  (Firebase rejected the link as
 *                                   `credential-already-in-use` — proven taken)
 *   link + succeeded   → created/linked (identity was unknown → account made,
 *                                   anon uid upgraded in place, board kept)
 *   signin             → isNewUser (a new identity DOES mint a record here)
 *   no operation       → isNewUser (marker predates the field, or a redirect we
 *                                   did not initiate — keep the old behaviour
 *                                   rather than assert "created" blind)
 */
export interface RedirectAuthSignals {
  operation?: 'link' | 'signin' | null;
  recoveredExistingAccount?: boolean;
  isNewUser: boolean;
}

export function mapRedirectAuthOutcome(
  signals: RedirectAuthSignals,
): Pick<AuthOutcome, 'account' | 'guestData'> {
  if (signals.operation === 'link') {
    // Linking is the ONLY leg that carries the guest board across, so it is also
    // the only one that may say 'linked'.
    const created = !signals.recoveredExistingAccount;
    return {
      account: created ? 'created' : 'existing',
      guestData: created ? 'linked' : 'none',
    };
  }
  if (signals.operation === 'signin') {
    // `signInWithRedirect` REPLACES the anon session — nothing is carried over
    // even when the account is brand new. 'none' matches v4Auth's CREATED_ALONE
    // for the desktop `signInWithPopup` sibling, and keeps a new-but-unlinked
    // account from claiming a board was saved.
    return { account: signals.isNewUser ? 'created' : 'existing', guestData: 'none' };
  }
  // Operation unknown — a marker written before this field existed. Reproduce
  // the previous mapping EXACTLY rather than guessing in either direction.
  return {
    account: signals.isNewUser ? 'created' : 'existing',
    guestData: signals.isNewUser ? 'linked' : 'none',
  };
}

interface AuthConfirmationContextValue {
  /** Record a verified outcome. Fires `auth_completed`, and — when the outcome
   *  warrants a message — shows the toast and fires `auth_confirmation_shown`. */
  confirm: (outcome: AuthOutcome) => void;
}

const AuthConfirmationContext = createContext<AuthConfirmationContextValue | null>(null);

/**
 * Tolerant of a missing provider ON PURPOSE: `V4SignInModal` is rendered by
 * isolated stories, the preview/journey harnesses, and unit tests with no App
 * provider stack. A throwing hook would break all of those; a no-op `confirm`
 * degrades cleanly (no toast, no analytics — exactly right off the real app).
 */
export function useAuthConfirmation(): AuthConfirmationContextValue {
  return useContext(AuthConfirmationContext) ?? NOOP;
}
const NOOP: AuthConfirmationContextValue = { confirm: () => {} };

/* ── The mobile-OAuth redirect handoff ────────────────────────────────────
 * A mobile Google/Apple sign-in leaves the page (signInWithRedirect), so the
 * modal is gone by the time auth completes on the return load. The outcome
 * surfaces in AuthGateContext's `consumeAuthRedirectResult` — but only Firebase's
 * `isNewUser` (created vs. existing) rides back with it. The board COUNT and the
 * entry context do not, so the modal stashes them here BEFORE initiating the
 * redirect, and AuthGate reads + clears them on return.
 */
const REDIRECT_KEY = 'thea:authConfirmPending';
const REDIRECT_MAX_AGE_MS = 10 * 60 * 1000; // mirror the redirect marker's ceiling

export interface RedirectConfirmationContext {
  entryContext: Exclude<AuthEntryContext, 'claim'>;
  boardCount: number;
  /**
   * True when this redirect was initiated from the "Don't lose {name}'s
   * board" capture sheet specifically (`V4CaptureSheet`), not from one of
   * the other doors that also pass boards (`BoardSignInBackdrop`, the
   * account menu, …). Lets AuthGate's redirect-return consumer fire
   * `capture_sheet_signed_up` only for sheet-driven auth, matching the
   * synchronous popup/email path's own gating (THE-966).
   */
  fromCaptureSheet?: boolean;
}

export function stashRedirectConfirmation(ctx: RedirectConfirmationContext): void {
  try {
    sessionStorage.setItem(
      REDIRECT_KEY,
      JSON.stringify({ ...ctx, ts: Date.now() }),
    );
  } catch {
    /* Safari private / quota — the confirmation is a nicety, never load-bearing. */
  }
}

export function clearRedirectConfirmation(): void {
  try {
    sessionStorage.removeItem(REDIRECT_KEY);
  } catch {
    /* ignore */
  }
}

/** Read + CLEAR the stash. Clears unconditionally so a bailed redirect can never
 *  leave a stale record that fires a confirmation on a later, unrelated load. */
export function readAndClearRedirectConfirmation(): RedirectConfirmationContext | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(REDIRECT_KEY);
  } catch {
    return null;
  }
  clearRedirectConfirmation();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<RedirectConfirmationContext> & { ts?: number };
    if (typeof parsed.ts === 'number' && Date.now() - parsed.ts > REDIRECT_MAX_AGE_MS) {
      return null; // too old — treat as absent
    }
    const entryContext =
      parsed.entryContext === 'guest_board' ? 'guest_board' : 'generic';
    const boardCount = typeof parsed.boardCount === 'number' ? parsed.boardCount : 0;
    return { entryContext, boardCount, fromCaptureSheet: parsed.fromCaptureSheet === true };
  } catch {
    return null;
  }
}

/* ── The toast ─────────────────────────────────────────────────────────── */

const AUTO_DISMISS_MS = 6000;

const Toast = styled.div`
  position: fixed;
  /* Clear the mobile tab bar / guide sticky bar like every other bottom
     toast (THE-920) — see bottomBarInset.ts. The var resolves to 0px on its
     own once no bar is registered. */
  bottom: calc(24px + env(safe-area-inset-bottom, 0px) + var(--thea-bottom-bar-inset, 0px));
  left: 50%;
  transform: translateX(-50%);
  z-index: 80;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: calc(100vw - 32px);
  padding: 12px 12px 12px 18px;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
  font-family: ${({ theme }) => theme.font.sans};
`;

const Message = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: hsl(var(--foreground));
`;

const DismissButton = styled.button`
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.color.mutedText};
  font-size: 18px;
  line-height: 1;
  padding: 6px;
  cursor: pointer;
  border-radius: 9999px;
  &:hover {
    background: ${({ theme }) => theme.color.creamLight};
  }
`;

export const AuthConfirmationProvider: React.FC<{
  children: ReactNode;
  /**
   * Injected home navigation for the LOSS COHORT (`welcome_back`): a guest who
   * signed into an account they already have is sent to their own boards instead
   * of being stranded on the now-orphaned guest-board URL. Injected (not a bare
   * `useNavigate`) so the provider stays router-decoupled — isolated stories/tests
   * render it with no Router and simply pass nothing (no nav, toast still asserts).
   */
  onWelcomeBackNavigate?: () => void;
}> = ({ children, onWelcomeBackNavigate }) => {
  const [message, setMessage] = useState<AuthConfirmationMessage | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    clearTimer();
    setMessage(null);
  }, [clearTimer]);

  const confirm = useCallback(
    (outcome: AuthOutcome) => {
      // `auth_completed` fires for EVERY verified outcome — including the
      // existing-account sign-in that shows no toast — so the funnel sees the
      // full picture, not only the confirmed ones.
      gaAuthCompleted({
        entryContext: outcome.entryContext,
        account: outcome.account,
        guestData: outcome.guestData,
      });
      // ENTRY CARRIED THROUGH SIGNUP (THE-746 Phase 4, PR 4.5 / THE-770). If
      // this auth began at the Fresh Find Fridays Unlock pill, its funnel fires
      // its terminal event here and disarms; for every other door this is a
      // no-op. Here and not at AuthGate's `onAuthed`, because `onAuthed` is
      // handed no outcome — this chokepoint is the one place that knows
      // created-vs-existing for BOTH the in-modal path and the mobile-redirect
      // return leg, and counting those apart is the point of the funnel.
      reportFreshFindUnlockAuth(outcome.account);
      const mapped = mapAuthConfirmation(outcome);
      if (!mapped) return;
      gaAuthConfirmationShown({
        entryContext: outcome.entryContext,
        messageKind: mapped.kind,
      });
      // Loss cohort → leave the orphaned board URL for their own boards FIRST, so
      // the results page never falls through to its "couldn't find this
      // recommendation" state; the toast (portalled outside the routes) rides the
      // navigation and lands on home.
      if (mapped.kind === 'welcome_back') onWelcomeBackNavigate?.();
      clearTimer();
      setMessage(mapped);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setMessage(null);
      }, AUTO_DISMISS_MS);
    },
    [clearTimer, onWelcomeBackNavigate],
  );

  useEffect(() => clearTimer, [clearTimer]);

  return (
    <AuthConfirmationContext.Provider value={{ confirm }}>
      {children}
      {message && typeof document !== 'undefined'
        ? createPortal(
            // role="status" + aria-live announces without stealing focus (§3.3);
            // non-modal, so the board/guide/page under it is fully usable.
            <Toast
              role="status"
              aria-live="polite"
              data-testid="auth-confirmation-toast"
              data-confirmation-kind={message.kind}
            >
              <Message>{message.message}</Message>
              <DismissButton type="button" aria-label="Dismiss" onClick={dismiss}>
                ×
              </DismissButton>
            </Toast>,
            document.body,
          )
        : null}
    </AuthConfirmationContext.Provider>
  );
};

export default AuthConfirmationProvider;
